// src/Admin/pages/AdminSupport.js
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Divider,
  Stack,
  Button,
  Chip,
  Alert,
  CircularProgress,
  TextField,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";

import {
  getAdminTickets,
  updateTicketStatus,
  getAdminDisputes,
  resolveDispute,
} from "../../api/adminApi";

/* --------------------------
   Helpers
-------------------------- */

function safeLower(v) {
  return String(v ?? "").toLowerCase();
}

function fmtDate(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return String(ts);
  }
}

function contains(hay, needle) {
  return safeLower(hay).includes(safeLower(needle));
}

function statusChipColor(status) {
  const s = safeLower(status);
  if (s === "open") return "warning";
  if (s === "in_review") return "info";
  if (s === "resolved") return "success";
  if (s === "closed") return "default";
  if (s === "denied") return "default";
  return "default";
}

export default function AdminSupport() {
  const location = useLocation();

  // ✅ OPTION A: route-driven pages (no tabs)
  // /admin/support -> tickets
  // /admin/support/disputes -> disputes
  const isDisputesView = location.pathname.startsWith("/admin/support/disputes");

  /* --------------------------
     Page state
  -------------------------- */

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [tickets, setTickets] = useState([]);
  const [disputes, setDisputes] = useState([]);

  // filters
  const [search, setSearch] = useState("");
  const [ticketStatusFilter, setTicketStatusFilter] = useState("all");
  const [disputeStatusFilter, setDisputeStatusFilter] = useState("all");

  /* --------------------------
     Ticket modal
  -------------------------- */
  const [ticketOpen, setTicketOpen] = useState(false);
  const [activeTicket, setActiveTicket] = useState(null);
  const [ticketNextStatus, setTicketNextStatus] = useState("open");

  /* --------------------------
     Dispute modal
  -------------------------- */
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [activeDispute, setActiveDispute] = useState(null);

  const [decision, setDecision] = useState("resolved"); // resolved | denied | partial_refund
  const [refundAmount, setRefundAmount] = useState("");
  const [decisionNotes, setDecisionNotes] = useState("");
  const [resolving, setResolving] = useState(false);

  /* --------------------------
     Data loading
  -------------------------- */

  const openTicketsCount = useMemo(
    () => tickets.filter((t) => safeLower(t.status) === "open").length,
    [tickets]
  );

  const openDisputesCount = useMemo(
    () => disputes.filter((d) => safeLower(d.status) === "open").length,
    [disputes]
  );

  async function loadAll() {
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      const [tix, dsp] = await Promise.all([getAdminTickets(), getAdminDisputes()]);
      setTickets(Array.isArray(tix) ? tix : []);
      setDisputes(Array.isArray(dsp) ? dsp : []);
    } catch (e) {
      setError(e?.message || "Failed to load support data.");
    } finally {
      setLoading(false);
    }
  }

  async function refreshAll() {
    setError("");
    setSuccess("");
    setRefreshing(true);
    try {
      const [tix, dsp] = await Promise.all([getAdminTickets(), getAdminDisputes()]);
      setTickets(Array.isArray(tix) ? tix : []);
      setDisputes(Array.isArray(dsp) ? dsp : []);
      setSuccess("Refreshed.");
      setTimeout(() => setSuccess(""), 1500);
    } catch (e) {
      setError(e?.message || "Failed to refresh support data.");
    } finally {
      setRefreshing(false);
    }
  }

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* --------------------------
     Filtering
  -------------------------- */

  const filteredTickets = useMemo(() => {
    return tickets
      .filter((t) => {
        if (!search) return true;
        return (
          contains(t.id, search) ||
          contains(t.subject, search) ||
          contains(t.customer_email, search) ||
          contains(t.courier_email, search) ||
          contains(t.order_id, search) ||
          contains(t.status, search)
        );
      })
      .filter((t) => {
        if (ticketStatusFilter === "all") return true;
        return safeLower(t.status) === safeLower(ticketStatusFilter);
      });
  }, [tickets, search, ticketStatusFilter]);

  const filteredDisputes = useMemo(() => {
    return disputes
      .filter((d) => {
        if (!search) return true;
        return (
          contains(d.id, search) ||
          contains(d.order_id, search) ||
          contains(d.customer_email, search) ||
          contains(d.courier_email, search) ||
          contains(d.reason, search) ||
          contains(d.status, search)
        );
      })
      .filter((d) => {
        if (disputeStatusFilter === "all") return true;
        return safeLower(d.status) === safeLower(disputeStatusFilter);
      });
  }, [disputes, search, disputeStatusFilter]);

  /* --------------------------
     Ticket actions
  -------------------------- */

  function openTicketModal(ticket) {
    setActiveTicket(ticket);
    setTicketNextStatus(ticket?.status || "open");
    setTicketOpen(true);
  }

  function closeTicketModal() {
    setTicketOpen(false);
    setActiveTicket(null);
  }

  async function applyTicketStatus() {
    if (!activeTicket?.id) return;
    setError("");
    setSuccess("");
    try {
      await updateTicketStatus(activeTicket.id, ticketNextStatus);
      setSuccess("Ticket updated.");
      closeTicketModal();
      refreshAll();
    } catch (e) {
      setError(e?.message || "Failed to update ticket.");
    }
  }

  /* --------------------------
     Dispute actions
  -------------------------- */

  function openDisputeModal(dispute) {
    setActiveDispute(dispute);
    setDecision("resolved");
    setRefundAmount("");
    setDecisionNotes("");
    setDisputeOpen(true);
  }

  function closeDisputeModal() {
    setDisputeOpen(false);
    setActiveDispute(null);
    setDecision("resolved");
    setRefundAmount("");
    setDecisionNotes("");
    setResolving(false);
  }

  async function applyDisputeResolution() {
    if (!activeDispute?.id) return;

    setError("");
    setSuccess("");
    setResolving(true);

    try {
      const payload = {
        decision,
        refund_amount:
          decision === "partial_refund" ? Number(refundAmount || 0) : null,
        notes: decisionNotes || "",
      };

      await resolveDispute(activeDispute.id, payload);

      setSuccess("Dispute resolved.");
      closeDisputeModal();
      refreshAll();
    } catch (e) {
      setError(e?.message || "Failed to resolve dispute.");
      setResolving(false);
    }
  }

  /* --------------------------
     Render helpers
  -------------------------- */

  const statusOptions = [
    { value: "all", label: "All" },
    { value: "open", label: "Open" },
    { value: "in_review", label: "In review" },
    { value: "resolved", label: "Resolved" },
    { value: "closed", label: "Closed" },
    { value: "denied", label: "Denied" },
  ];

  /* --------------------------
     UI
  -------------------------- */

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
              Support
            </Typography>
            <Typography color="text.secondary">
              Tickets + disputes. Everything here should be traceable and resolvable.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={`Open tickets: ${openTicketsCount}`}
              color="success"
              variant="outlined"
            />
            <Chip
              label={`Open disputes: ${openDisputesCount}`}
              color="success"
              variant="outlined"
            />
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={refreshAll}
              disabled={refreshing}
            >
              {refreshing ? "Refreshing..." : "Refresh"}
            </Button>
          </Stack>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* ✅ OPTION A: No tabs here. Left sidebar decides the page */}
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          {isDisputesView ? "Disputes" : "Tickets"}
        </Typography>
      </Paper>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {/* Loading */}
      {loading ? (
        <Paper sx={{ p: 6 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress />
            <Typography>Loading support data...</Typography>
          </Stack>
        </Paper>
      ) : (
        <Paper sx={{ p: 3 }}>
          {/* Filters */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            sx={{ mb: 2 }}
          >
            <TextField
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              fullWidth
            />

            {!isDisputesView ? (
              <TextField
                select
                label="Ticket status"
                value={ticketStatusFilter}
                onChange={(e) => setTicketStatusFilter(e.target.value)}
                sx={{ width: { xs: "100%", md: 240 } }}
              >
                {statusOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                select
                label="Dispute status"
                value={disputeStatusFilter}
                onChange={(e) => setDisputeStatusFilter(e.target.value)}
                sx={{ width: { xs: "100%", md: 240 } }}
              >
                {statusOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Stack>

          {/* Tables */}
          {!isDisputesView ? (
            <>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
                Tickets
              </Typography>

              {filteredTickets.length === 0 ? (
                <Alert severity="info">No tickets found for the current filters.</Alert>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Order</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Courier</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Updated</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTickets.map((t) => (
                      <TableRow key={t.id} hover>
                        <TableCell>{t.id}</TableCell>
                        <TableCell>{t.subject || "—"}</TableCell>
                        <TableCell>{t.order_id || "—"}</TableCell>
                        <TableCell>{t.customer_email || "—"}</TableCell>
                        <TableCell>{t.courier_email || "—"}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={t.status || "—"}
                            color={statusChipColor(t.status)}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{fmtDate(t.updated_at || t.created_at)}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => openTicketModal(t)}
                            title="View / update"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          ) : (
            <>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
                Disputes
              </Typography>

              {filteredDisputes.length === 0 ? (
                <Alert severity="info">
                  No disputes found for the current filters.
                </Alert>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Order</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Courier</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Updated</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredDisputes.map((d) => (
                      <TableRow key={d.id} hover>
                        <TableCell>{d.id}</TableCell>
                        <TableCell>{d.order_id || "—"}</TableCell>
                        <TableCell>{d.reason || "—"}</TableCell>
                        <TableCell>{d.customer_email || "—"}</TableCell>
                        <TableCell>{d.courier_email || "—"}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={d.status || "—"}
                            color={statusChipColor(d.status)}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{fmtDate(d.updated_at || d.created_at)}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => openDisputeModal(d)}
                            title="View / resolve"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </Paper>
      )}

      {/* Ticket dialog */}
      <Dialog open={ticketOpen} onClose={closeTicketModal} maxWidth="md" fullWidth>
        <DialogTitle>Ticket</DialogTitle>
        <DialogContent dividers>
          {!activeTicket ? (
            <Typography color="text.secondary">No ticket selected.</Typography>
          ) : (
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Chip label={`ID: ${activeTicket.id}`} variant="outlined" />
                <Chip
                  label={`Status: ${activeTicket.status || "—"}`}
                  variant="outlined"
                />
                {activeTicket.order_id ? (
                  <Chip
                    label={`Order: ${activeTicket.order_id}`}
                    variant="outlined"
                  />
                ) : null}
              </Stack>

              <Typography variant="h6">{activeTicket.subject || "—"}</Typography>

              <Typography color="text.secondary">
                Customer: {activeTicket.customer_email || "—"} | Courier:{" "}
                {activeTicket.courier_email || "—"}
              </Typography>

              <Typography sx={{ whiteSpace: "pre-wrap" }}>
                {activeTicket.message || activeTicket.description || "—"}
              </Typography>

              <Divider />

              <TextField
                select
                label="Update status"
                value={ticketNextStatus}
                onChange={(e) => setTicketNextStatus(e.target.value)}
              >
                {statusOptions
                  .filter((o) => o.value !== "all")
                  .map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
              </TextField>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeTicketModal}>Close</Button>
          <Button
            variant="contained"
            onClick={applyTicketStatus}
            disabled={!activeTicket}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dispute dialog */}
      <Dialog open={disputeOpen} onClose={closeDisputeModal} maxWidth="md" fullWidth>
        <DialogTitle>Dispute</DialogTitle>
        <DialogContent dividers>
          {!activeDispute ? (
            <Typography color="text.secondary">No dispute selected.</Typography>
          ) : (
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Chip label={`ID: ${activeDispute.id}`} variant="outlined" />
                <Chip
                  label={`Status: ${activeDispute.status || "—"}`}
                  variant="outlined"
                />
                {activeDispute.order_id ? (
                  <Chip
                    label={`Order: ${activeDispute.order_id}`}
                    variant="outlined"
                  />
                ) : null}
              </Stack>

              <Typography variant="h6">
                {activeDispute.reason || "Dispute"}
              </Typography>

              <Typography color="text.secondary">
                Customer: {activeDispute.customer_email || "—"} | Courier:{" "}
                {activeDispute.courier_email || "—"}
              </Typography>

              <Typography sx={{ whiteSpace: "pre-wrap" }}>
                {activeDispute.details || activeDispute.message || "—"}
              </Typography>

              <Divider />

              <TextField
                select
                label="Decision"
                value={decision}
                onChange={(e) => setDecision(e.target.value)}
              >
                <MenuItem value="resolved">Resolved</MenuItem>
                <MenuItem value="denied">Denied</MenuItem>
                <MenuItem value="partial_refund">Partial refund</MenuItem>
              </TextField>

              {decision === "partial_refund" ? (
                <TextField
                  label="Refund amount"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  type="number"
                  inputProps={{ min: 0, step: "0.01" }}
                />
              ) : null}

              <TextField
                label="Notes"
                value={decisionNotes}
                onChange={(e) => setDecisionNotes(e.target.value)}
                multiline
                minRows={3}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDisputeModal} disabled={resolving}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={applyDisputeResolution}
            disabled={!activeDispute || resolving}
          >
            {resolving ? "Saving..." : "Finalize"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
